import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useI18n } from '@/i18n';
import { useResilientAction } from '@/hooks/useResilientAction';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FaCircleArrowLeft, FaCircleUser, FaPhone, FaCamera, FaFloppyDisk } from "react-icons/fa6";
import { Card, CardContent, CardHeader, CardTitle /* CardDescription */ } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function CustomerProfile() {
  const { t, currentLang } = useI18n();
  const navigate = useNavigate();
  const { profile, updateProfile, uploadAvatar, fetchProfile } = useAuthStore();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
  });

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const { isLoading: isSaving, execute: resilientSave } = useResilientAction(
    async (signal: AbortSignal) => {
      const success = await updateProfile(formData, signal);
      if (success) {
        toast.success(t('profile.update_success'));
        await fetchProfile();
      } else {
        toast.error(t('profile.update_error'));
      }
      return success;
    }
  );

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await resilientSave();
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB
        toast.error('Image size must be less than 10MB');
        return;
    }

    // Instant Preview
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);

    setIsUploading(true);
    try {
      const publicUrl = await uploadAvatar(file);
      if (publicUrl) {
        const success = await updateProfile({ avatar_url: publicUrl });
        if (success) {
          toast.success('Avatar updated successfully');
          await fetchProfile();
        }
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error(t('profile.upload_error'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-white font-sans">
      <div className="animate-fade-in pb-24 lg:pb-10">
        {/* Premium Header — Gold Standard */}
        <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl hover:bg-gray-50 text-black transition-all active:scale-95"
              onClick={() => navigate(-1)}
            >
              <FaCircleArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-lg font-black tracking-tight text-center flex-1">
              {currentLang === 'bn' ? 'প্রোফাইল' : 'Profile'}
            </h1>
            <div className="w-10" /> {/* Spacer */}
          </div>
        </header>

        {/* Avatar Upload */}
        <div className="flex flex-col items-center px-6 pt-4 pb-2">
          <div className="relative inline-block group">
            <div className="w-28 h-28 rounded-full border-4 border-white shadow-2xl overflow-hidden bg-slate-50 flex items-center justify-center">
              {(previewUrl || profile?.avatar_url) ? (
                <img 
                  src={previewUrl || profile?.avatar_url || ''} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <FaCircleUser className="w-12 h-12 text-slate-300" />
              )}
              {isUploading && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-full">
                   <Loader2 className="h-8 w-8 text-white animate-spin" />
                </div>
              )}
            </div>
            <button 
              onClick={handleAvatarClick}
              className="absolute bottom-1 right-1 bg-primary text-white p-2 rounded-full shadow-lg hover:scale-110 active:scale-95 transition-transform border-4 border-white"
              disabled={isUploading}
            >
              <FaCamera className="h-4 w-4" />
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              className="hidden" 
              accept="image/*"
            />
          </div>
        </div>

        {/* Profile Information Form */}
        <div className="px-4">
          <div className="max-w-2xl mx-auto">
          <Card className="border-none shadow-premium rounded-3xl bg-white overflow-hidden border border-slate-100">
            <CardHeader className="pt-0 pb-0">
              <CardTitle className="text-2xl font-black text-slate-900 flex items-center gap-2">
                <FaCircleUser className="h-6 w-6 text-primary" />
                Profile Information
              </CardTitle>
              {/* <CardDescription className="text-black text-[10.9px] md:text-[13.5px] lg:text-[14px] font-medium">Update your details for a personalized shopping experience</CardDescription> */}
            </CardHeader>
            <CardContent className="pb-8">
              <form onSubmit={handleSave} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-black text-black uppercase tracking-widest ml-1">Full Name</Label>
                    <div className="relative group">
                      <FaCircleUser className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-300 group-focus-within:text-primary transition-colors" />
                      <Input 
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        className="pl-12 h-14 bg-slate-50 border-none focus-visible:ring-2 focus-visible:ring-primary rounded-2xl font-medium text-black placeholder:text-slate-300 transition-all"
                        placeholder="e.g. Abdullah Al Mamun"
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-black text-black uppercase tracking-widest ml-1">Phone Number</Label>
                    <div className="relative group">
                      <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-300 group-focus-within:text-primary transition-colors" />
                      <Input 
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="pl-12 h-14 bg-slate-50 border-none focus-visible:ring-2 focus-visible:ring-primary rounded-2xl font-medium text-black placeholder:text-slate-300 transition-all"
                        placeholder="01XXXXXXXXX"
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                </div>

                   {/* <p className="text-[9px] md:text-[10px] lg:text-[10.9px] text-black mt-2 ml-2 font-medium bg-white w-fit px-0 py-1 rounded-full">Profile information is used for delivery and communication purposes</p> */}

                <div className="pt-2">
                  <Button type="submit" className="w-full md:w-fit h-14 px-12 rounded-2xl font-black text-lg shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all" disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaFloppyDisk className="mr-2 h-6 w-6" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
